import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, FileCheck } from "lucide-react";
import apiClient from "@/api/client";
import { Button } from "@/components/ui/Button";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { Input } from "@/components/ui/Input";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { useAddOfficialFee } from "../hooks/useOfficialFees";
import { isValidAccountNumber, formatAccountNumber } from "../utils/accountNumber";
import { FEE_APPROVAL_THRESHOLD, isDescriptionRequired } from "../utils/feeThreshold";
import { OfficeAutocomplete } from "../components/OfficeAutocomplete";
import { officialFeesApi } from "../api/officialFees";
import type { OfficeSuggestion } from "../api/officialFees";

const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

interface Company {
  id: string;
  short_code: string;
  name: string;
}

const schema = z.object({
  project: z.string().min(1, "Wybierz przedsięwzięcie"),
  order: z.string().optional(),
  paying_company: z.string().min(1, "Wybierz spółkę płacącą"),
  office_name: z.string().min(2, "Podaj nazwę urzędu"),
  office_account_number: z
    .string()
    .min(1, "Podaj numer konta urzędu")
    .refine(isValidAccountNumber, "Numer konta musi zawierać 26 cyfr (opcjonalnie z przedrostkiem PL)"),
  office_address: z.string().min(5, "Podaj adres urzędu"),
  transfer_name: z.string().min(2, "Podaj tytuł przelewu"),
  amount: z
    .string()
    .min(1, "Podaj kwotę")
    .refine((v) => !isNaN(parseFloat(v)) && parseFloat(v) > 0, "Kwota musi być dodatnia"),
  description: z.string().optional(),
  investor_name_required_in_title: z.boolean().default(false),
  is_power_of_attorney: z.boolean().default(false),
})
  // Opłata dodawana jako już zapłacona idzie tym samym serializerem co prośba,
  // więc obowiązuje ją ten sam próg opisu.
  .superRefine((val, ctx) => {
    if (isDescriptionRequired(val.amount) && !val.description?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Opis jest obowiązkowy dla kwot powyżej ${FEE_APPROVAL_THRESHOLD} zł`,
        path: ["description"],
      });
    }
  });

type FormData = z.infer<typeof schema>;

// Same source as the request form — see the note there on why not /projects/.
function useProjects() {
  return useQuery({
    queryKey: ["official-fee-available-projects"],
    queryFn: () => officialFeesApi.availableProjects(),
  });
}

function useCompanies() {
  return useQuery({
    queryKey: ["gf-company-choices"],
    queryFn: () =>
      apiClient.get<{ data: Company[] }>("/group-finance/company-choices/").then((r) => r.data.data ?? []),
  });
}

export default function OfficialFeeAddPage() {
  const navigate = useNavigate();
  const { data: projects = [], isLoading: projectsLoading } = useProjects();
  const { data: companies = [], isLoading: companiesLoading } = useCompanies();
  const addMut = useAddOfficialFee();

  const [paymentFiles, setPaymentFiles] = useState<File[]>([]);
  const [paymentFileError, setPaymentFileError] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);

  const projectCompanyMap = useMemo(() => {
    const map = new Map<string, string | null>();
    for (const p of projects) map.set(p.id, p.company);
    return map;
  }, [projects]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      project: "",
      order: "",
      paying_company: "",
      office_name: "",
      office_account_number: "",
      office_address: "",
      transfer_name: "",
      amount: "",
      description: "",
      investor_name_required_in_title: false,
      is_power_of_attorney: false,
    },
  });

  const selectedProjectId = watch("project");

  useEffect(() => {
    if (!selectedProjectId) return;
    const company = projectCompanyMap.get(selectedProjectId);
    if (company) setValue("paying_company", company);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectId]);

  const selectedProject = projects.find((p) => p.id === selectedProjectId);
  const lockedCompanyId = selectedProject?.company ?? null;
  const lockedCompany = lockedCompanyId ? companies.find((c) => c.id === lockedCompanyId) : null;
  const companyLockedByProject = !!lockedCompanyId;

  const { onBlur: accountOnBlurRhf, ...accountReg } = register("office_account_number");
  function handleAccountBlur(e: React.FocusEvent<HTMLInputElement>) {
    const formatted = formatAccountNumber(e.target.value);
    e.target.value = formatted;
    accountOnBlurRhf(e);
    setValue("office_account_number", formatted, { shouldValidate: true });
  }

  const officeName = watch("office_name");
  const descriptionRequired = isDescriptionRequired(watch("amount"));

  function handleOfficeSuggestionSelect(s: OfficeSuggestion) {
    setValue("office_name", s.office_name, { shouldValidate: true });
    setValue("office_account_number", s.office_account_number, { shouldValidate: true });
    setValue("office_address", s.office_address, { shouldValidate: true });
  }

  function handlePaymentFilesChange(next: File[]) {
    const nonPdf = next.find((f) => !f.name.toLowerCase().endsWith(".pdf"));
    if (nonPdf) {
      setPaymentFileError(`Plik "${nonPdf.name}" musi być w formacie PDF.`);
      return;
    }
    setPaymentFileError(null);
    setPaymentFiles(next);
  }

  function handleAttachmentsChange(next: File[]) {
    const oversize = next.find((f) => f.size > MAX_ATTACHMENT_BYTES);
    if (oversize) {
      setAttachmentError(`Plik "${oversize.name}" przekracza limit 25 MB.`);
      return;
    }
    setAttachmentError(null);
    setAttachments(next);
  }

  async function onSubmit(data: FormData) {
    await addMut.mutateAsync({
      data: {
        project: data.project,
        order: data.order || undefined,
        paying_company: data.paying_company,
        office_name: data.office_name,
        office_account_number: data.office_account_number,
        office_address: data.office_address,
        transfer_name: data.transfer_name,
        amount: data.amount,
        description: data.description,
        investor_name_required_in_title: data.investor_name_required_in_title,
        is_power_of_attorney: data.is_power_of_attorney,
      },
      paymentFiles,
      attachmentFiles: attachments,
    });
    navigate("/official-fees");
  }

  if (projectsLoading || companiesLoading) {
    return <div className="p-6 text-[var(--color-text-secondary)]">Ładowanie...</div>;
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <button
        onClick={() => navigate("/official-fees")}
        className="flex items-center gap-1 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] mb-4"
      >
        <ArrowLeft size={14} />
        Wróć do listy
      </button>

      <h1 className="text-xl font-semibold text-[var(--color-text-primary)] mb-2">
        Dodaj opłatę urzędową
      </h1>
      <p className="text-sm text-[var(--color-text-secondary)] mb-6">
        Opłata zostanie od razu ze statusem <strong>Potwierdzenie przesłane</strong>.
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Project */}
        <SearchableSelect
          label="Przedsięwzięcie *"
          placeholder="— wybierz —"
          options={projects
            .filter((p) => p.is_active)
            .map((p) => ({ value: p.id, label: `${p.short_name} — ${p.name}` }))}
          error={errors.project?.message}
          value={selectedProjectId}
          {...register("project")}
        />

        {/* Paying company */}
        <div>
          <label className="block text-sm font-medium text-[var(--color-text-primary)] mb-1">
            Spółka płacąca <span className="text-red-500">*</span>
          </label>
          {companyLockedByProject ? (
            <>
              <input type="hidden" {...register("paying_company")} />
              <div className="input-base w-full bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] cursor-not-allowed">
                {lockedCompany
                  ? `${lockedCompany.short_code} — ${lockedCompany.name}`
                  : selectedProject?.company_name || "—"}
              </div>
              <p className="text-xs text-[var(--color-text-secondary)] mt-1">
                Ustawione automatycznie na podstawie przedsięwzięcia.
              </p>
            </>
          ) : (
            <SearchableSelect
              placeholder="— wybierz spółkę —"
              options={companies.map((c) => ({ value: c.id, label: `${c.short_code} — ${c.name}` }))}
              value={watch("paying_company")}
              {...register("paying_company")}
            />
          )}
          {errors.paying_company && (
            <p className="text-xs text-red-500 mt-1">{errors.paying_company.message}</p>
          )}
        </div>

        {/* Office name */}
        <div>
          <label className="block text-sm font-medium text-[var(--color-text-primary)] mb-1">
            Nazwa urzędu <span className="text-red-500">*</span>
          </label>
          <OfficeAutocomplete
            value={officeName}
            onChange={(v) => setValue("office_name", v, { shouldValidate: false })}
            onSelect={handleOfficeSuggestionSelect}
            placeholder="np. Urząd Miasta Wrocław"
            hasError={!!errors.office_name}
          />
          {errors.office_name && (
            <p className="text-xs text-red-500 mt-1">{errors.office_name.message}</p>
          )}
        </div>

        {/* Account number */}
        <div>
          <label className="block text-sm font-medium text-[var(--color-text-primary)] mb-1">
            Numer konta urzędu <span className="text-red-500">*</span>
          </label>
          <Input
            {...accountReg}
            onBlur={handleAccountBlur}
            placeholder="PL61 1090 1014 0000 0712 1981 2874"
            className="font-mono"
          />
          {errors.office_account_number && (
            <p className="text-xs text-red-500 mt-1">{errors.office_account_number.message}</p>
          )}
        </div>

        {/* Office address */}
        <div>
          <label className="block text-sm font-medium text-[var(--color-text-primary)] mb-1">
            Adres urzędu <span className="text-red-500">*</span>
          </label>
          <textarea
            {...register("office_address")}
            className="w-full rounded border border-[var(--color-border)] px-3 py-2 text-sm bg-[var(--color-bg-input)] text-[var(--color-text-primary)] min-h-[64px]"
            placeholder="ul. Szamocka 3, 01-748 Warszawa"
          />
          {errors.office_address && (
            <p className="text-xs text-red-500 mt-1">{errors.office_address.message}</p>
          )}
        </div>

        {/* Transfer name */}
        <div>
          <label className="block text-sm font-medium text-[var(--color-text-primary)] mb-1">
            Tytuł przelewu <span className="text-red-500">*</span>
          </label>
          <Input {...register("transfer_name")} placeholder="np. Opłata za pełnomocnictwo" />
          {errors.transfer_name && (
            <p className="text-xs text-red-500 mt-1">{errors.transfer_name.message}</p>
          )}
        </div>

        <div>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              {...register("investor_name_required_in_title")}
              className="rounded border-[var(--color-border)] accent-[var(--color-primary)] w-4 h-4"
            />
            <span className="text-sm text-[var(--color-text-primary)]">
              Wymagana nazwa inwestora w tytule
            </span>
          </label>
        </div>

        <div>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              {...register("is_power_of_attorney")}
              className="rounded border-[var(--color-border)] accent-[var(--color-primary)] w-4 h-4"
            />
            <span className="text-sm text-[var(--color-text-primary)]">
              Opłata za pełnomocnictwo
            </span>
          </label>
        </div>

        {/* Amount */}
        <div>
          <label className="block text-sm font-medium text-[var(--color-text-primary)] mb-1">
            Kwota (PLN) <span className="text-red-500">*</span>
          </label>
          <Input
            {...register("amount")}
            type="number"
            step="0.01"
            min="0.01"
            placeholder="250.00"
            className="font-mono [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          />
          {errors.amount && (
            <p className="text-xs text-red-500 mt-1">{errors.amount.message}</p>
          )}
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-[var(--color-text-primary)] mb-1">
            {descriptionRequired ? (
              <>
                Opis <span className="text-red-500">*</span>
              </>
            ) : (
              "Opis (opcjonalny)"
            )}
          </label>
          <textarea
            {...register("description")}
            className="w-full rounded border border-[var(--color-border)] px-3 py-2 text-sm bg-[var(--color-bg-input)] text-[var(--color-text-primary)] min-h-[64px]"
            placeholder={
              descriptionRequired
                ? `Za co jest ta opłata? (wymagane powyżej ${FEE_APPROVAL_THRESHOLD} zł)`
                : "Dodatkowe informacje..."
            }
          />
          {errors.description && (
            <p className="text-xs text-red-500 mt-1">{errors.description.message}</p>
          )}
        </div>

        {/* Payment confirmation files */}
        <div className="rounded-lg border border-[var(--color-border)] p-4 space-y-2 bg-[var(--color-bg-elevated)]">
          <div className="flex items-center gap-2 mb-1">
            <FileCheck size={15} className="text-[var(--color-primary)]" />
            <label className="text-sm font-medium text-[var(--color-text-primary)]">
              Potwierdzenie przelewu (PDF, opcjonalnie)
            </label>
          </div>
          <p className="text-xs text-[var(--color-text-secondary)]">
            Dołącz plik PDF potwierdzający wykonanie przelewu.
          </p>
          <FileDropZone
            files={paymentFiles}
            onFilesChange={handlePaymentFilesChange}
            multiple
            accept=".pdf"
            label="Przeciągnij pliki PDF lub kliknij, aby wybrać"
          />
          {paymentFileError && <p className="text-xs text-red-500">{paymentFileError}</p>}
        </div>

        {/* Attachments */}
        <div>
          <label className="block text-sm font-medium text-[var(--color-text-primary)] mb-1">
            Załączniki (opcjonalnie)
          </label>
          <p className="text-xs text-[var(--color-text-secondary)] mb-2">
            Inne pliki do przechowania razem z opłatą. Limit 25 MB na plik.
          </p>
          <FileDropZone files={attachments} onFilesChange={handleAttachmentsChange} multiple />
          {attachmentError && <p className="text-xs text-red-500 mt-1">{attachmentError}</p>}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" type="button" onClick={() => navigate("/official-fees")}>
            Anuluj
          </Button>
          <Button type="submit" disabled={isSubmitting || addMut.isPending}>
            {isSubmitting || addMut.isPending ? "Dodawanie..." : "Dodaj opłatę"}
          </Button>
        </div>
      </form>
    </div>
  );
}
